import {H5ForecastData, H5ForecastFrame, H5WeatherForecastData} from 'src/lib/api/h5';
import {ForecastTime} from 'src/lib/api/series';
import {
  closestForecastTimeWithinDay,
  formatDateShortFull,
  getLatestForecastTime,
} from 'src/lib/dates';

export const formatForecastTime = (forecast: ForecastTime, timeZone: string) =>
  `${formatDateShortFull(forecast.initializationTime, timeZone)} · ${formatDateShortFull(forecast.issueTime, timeZone)}`;

const forecastKey = (initTime: Date, issueTime: Date) =>
  `${initTime.toISOString()}:${issueTime.toISOString()}`;

const hasValues = (validTimes: Date[], columns: (number | null)[][]) =>
  validTimes.length > 0 && columns.some((column) => column.some((value) => value !== null));

// Bounds can be present while the central line is entirely missing, and a band alone is not a
// usable forecast.
const frameHasData = (frame: H5ForecastFrame) => {
  const centralVariables = frame.variables.filter(
    (variable) => variable.role === 'central-tendency'
  );
  const columns =
    centralVariables.length > 0
      ? centralVariables.map((variable) => frame.values[variable.name] ?? [])
      : Object.values(frame.values);
  return hasValues(frame.validTimes, columns);
};

// The selected runs the response holds nothing for in the window.
function selectedWithoutData(
  selectedForecastTimes: ForecastTime[],
  keysWithData: string[],
  keyOf: (forecast: ForecastTime) => string
): ForecastTime[] {
  const withData = new Set(keysWithData);
  return selectedForecastTimes.filter((forecast) => !withData.has(keyOf(forecast)));
}

export function forecastTimesWithoutData(
  selectedForecastTimes: ForecastTime[],
  forecastData: H5ForecastData
): ForecastTime[] {
  return selectedWithoutData(
    selectedForecastTimes,
    forecastData.forecasts
      .filter(frameHasData)
      .map((frame) => forecastKey(frame.initTime, frame.issueTime)),
    (forecast) => forecastKey(forecast.initializationTime, forecast.issueTime)
  );
}

// A weather run has no issue time of its own, so its init time alone names it.
export function weatherForecastTimesWithoutData(
  selectedForecastTimes: ForecastTime[],
  weatherData: H5WeatherForecastData
): ForecastTime[] {
  return selectedWithoutData(
    selectedForecastTimes,
    weatherData.forecasts
      .filter((frame) => hasValues(frame.validTimes, Object.values(frame.values)))
      .map((frame) => frame.initTime.toISOString()),
    (forecast) => forecast.initializationTime.toISOString()
  );
}

const sameInit = (a: ForecastTime, b: ForecastTime) =>
  a.initializationTime.getTime() === b.initializationTime.getTime();

// Another forecast's run for each selected run, as the legacy chart pairs weather with flow: the
// nearest run within a day, except that selecting the newest flow run (`latestAvailable`) shows
// each source's newest run however far apart they are, so a late flow run still shows today's
// weather. Two flow runs from one day share that day's weather run.
export function nearestForecastTimes(
  selectedForecastTimes: ForecastTime[],
  candidates: ForecastTime[],
  latestAvailable?: ForecastTime
): ForecastTime[] {
  const nearest = new Set<ForecastTime>();
  selectedForecastTimes.forEach((selected) => {
    if (latestAvailable && sameInit(selected, latestAvailable)) {
      const newest = getLatestForecastTime(candidates);
      if (newest) nearest.add(newest);
      return;
    }
    const closest = closestForecastTimeWithinDay(selected, candidates);
    if (closest) nearest.add(closest);
  });
  return Array.from(nearest);
}
